import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Form, Progress, Spin, message } from 'antd';
import DspxModal from '../../../common/DspxModal/DspxModal';
import { ChessByFileForm } from '../../forms/ChessByFileForm/ChessFormByFile';

import {
  downloadFlatsGridTemplate,
  previewFlatsGridByFile,
} from '../../../../requests/flatsGridByFile';
import { useUploadMutation } from '../../../../../../hooks/useUploadMutation';
import { useFlatsGridTemplatesQuery } from '../../../../queries/useFlatsGridTemplates';
import { setFlatsGridPreviewData } from '../../../../store/actions/housingStockActions';
import { getPreparedFlatsGrid } from '../../../../pages/HousePage/utils';
import { texts } from '../../../../constants/texts';
import { fieldNames } from '../../forms/ChessByFileForm/constants';
import { downloadHousingStockCommonFile } from '../../../../requests/commonFile';

import styles from './ChessByFileModal.module.scss';

const CHESS_IMPORT_USER_GUIDE_FILENAME = 'Инструкция на шахматки.pdf';

const ChessByFileModalInner = ({
  form,
  onCancel,
  onSuccess,
  address,
  ...props
}) => {
  const dispatch = useDispatch();

  const { resetFields, validateFieldsAndScroll } = form;
  const currentHouse = useSelector(
    (state) => state.housingStockStore.currentHouse
  );

  const {
    mutation: previewFlatsGridMutation,
    progress: uploadProgress,
    abort: abortUpload,
    resetProgress,
  } = useUploadMutation(
    (values, extraConfig) => previewFlatsGridByFile(values, extraConfig),
    {
      onSuccess: (responseData, values) => {
        resetProgress();
        // request is cancelled
        if (!responseData) {
          return;
        }
        if (Array.isArray(responseData) && !responseData.length) {
          message.error('Некорректный файл. Не удалось построить шахматку');
        }
        const { flatsGridData } = getPreparedFlatsGrid(responseData);
        dispatch(
          setFlatsGridPreviewData({
            data: flatsGridData,
            file: values.file,
            houseId: values.houseId,
          })
        );
        onSuccess();
      },
    }
  );

  const progressStatus = useMemo(() => {
    if (previewFlatsGridMutation.isLoading && uploadProgress < 100)
      return 'active';
    if (uploadProgress === 100) return 'success';
    if (previewFlatsGridMutation.error) return 'exception';
  }, [
    previewFlatsGridMutation.error,
    previewFlatsGridMutation.isLoading,
    uploadProgress,
  ]);

  const handleCancel = () => {
    abortUpload();
    resetFields();
    onCancel();
  };

  const handleSubmit = () => {
    validateFieldsAndScroll(async (err, fieldValues) => {
      if (err) {
        return;
      }
      const values = {
        houseId: currentHouse?.houseId,
        file: fieldValues[fieldNames.files][0].originFileObj,
      };
      await previewFlatsGridMutation.mutateAsync(values);
    });
  };

  const chessTemplatesQuery = useFlatsGridTemplatesQuery();

  return (
    <DspxModal
      title={texts.addNewHouse}
      okText={texts.formFlatsGrid}
      cancelText={texts.cancel}
      onOk={handleSubmit}
      onCancel={handleCancel}
      // cancelButtonProps={{
      //   disabled: previewFlatsGridMutation.isLoading,
      // }}
      confirmLoading={previewFlatsGridMutation.isLoading}
      destroyOnClose
      maskClosable={false}
      {...props}
    >
      <ChessByFileForm
        form={form}
        address={address}
        progress={
          Boolean(uploadProgress) &&
          (uploadProgress < 100 ? (
            <Progress
              size="small"
              percent={uploadProgress}
              status={progressStatus}
            />
          ) : (
            uploadProgress === 100 && (
              <div>Файл загружен. Подождите, идёт обработка файла</div>
            )
          ))
        }
      >
        <div>
          Выполните пошагово инструкцию:{' '}
          <Button
            type="link"
            onClick={() => {
              downloadHousingStockCommonFile(CHESS_IMPORT_USER_GUIDE_FILENAME);
            }}
          >
            {CHESS_IMPORT_USER_GUIDE_FILENAME}
          </Button>
        </div>

        {chessTemplatesQuery.isFetching && <Spin size="small" />}
        {chessTemplatesQuery.isError && <div>Ошибка загрузки примеров</div>}
        {Array.isArray(chessTemplatesQuery.data) &&
          chessTemplatesQuery.data.length > 0 && (
            <>
              <div>
                Ознакомьтесь с примерами загружаемого файла:
                <ul className={styles.templatesList}>
                  {chessTemplatesQuery.data.map((template, index) => (
                    <li key={template.id}>
                      <Button
                        type="link"
                        onClick={() =>
                          downloadFlatsGridTemplate({
                            id: template.id,
                            fileName:
                              template.name || `Пример${index + 1}.xlsx`,
                          })
                        }
                      >
                        {template.name || `Пример${index + 1}.xlsx`}
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
      </ChessByFileForm>
    </DspxModal>
  );
};

ChessByFileModalInner.propTypes = {
  address: PropTypes.string.isRequired,
  form: PropTypes.any.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export const ChessByFileModal = Form.create({ name: 'chess_form_by_file' })(
  ChessByFileModalInner
);
